/**
 * A run's refused rows as a CSV the registrar fixes in a spreadsheet.
 *
 * A closed-term run read from IMAM's records template is written back in that
 * template, reversing the reader's mapping, with each row's reason in a
 * trailing `reason` column, so the corrected file reads straight back into the
 * records file. The rows a refused row refers to come with it, with no reason:
 * the server resolves a row's keys only to rows in the same file. Any other run
 * is written as each refused row's kind, key, reason and recorded fields.
 */
import { receiptRow, type EntityKind, type ImportBatch, type ImportReceipt } from "@/lib/api/imports";
import { RECORD_COLUMNS, RECORD_KIND_COLUMNS } from "@/lib/historical-records-source";
import { composite, parentsOf, sourceEvidence, withParents } from "@/lib/refused-rows";

type RecordColumn = (typeof RECORD_COLUMNS)[number];
const KIND_ORDER: readonly EntityKind[] = ["term", "faculty", "course", "student", "enrollment", "outcome"];
/** The reader's column-to-field renames, reversed here. */
const DATA_FIELD: Partial<Record<EntityKind, Partial<Record<RecordColumn, string>>>> = { student: { name: "legal_name" } };

function csvCell(value: string): string {
  return /[",\r\n]/.test(value) ? `"${value.replaceAll('"', '""')}"` : value;
}

function cellOf(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (Array.isArray(value) && value.every((v) => typeof v === "string")) return value.join(";");
  return typeof value === "object" ? JSON.stringify(value) : String(value);
}

/** A receipt read from the records template: its evidence names its kind and file line. */
export function isTemplateReceipt(receipt: ImportReceipt): boolean {
  return receipt.source_ref?.record === receipt.entity_kind && typeof receipt.source_ref?.line === "number";
}

/** A template row's cells: from its recorded data where it has some, else from the columns as the file wrote them. */
export function templateCells(receipt: ImportReceipt): Partial<Record<RecordColumn, string>> {
  const row = receiptRow(receipt);
  const evidence = receipt.source_ref ?? {};
  const cells: Partial<Record<RecordColumn, string>> = { record: receipt.entity_kind, key: receipt.import_key };
  for (const column of RECORD_KIND_COLUMNS[receipt.entity_kind]) {
    const value = !row ? evidence[column]
      : receipt.entity_kind === "faculty" ? evidence.name ?? row.data?.display_name
      : row.data?.[DATA_FIELD[receipt.entity_kind]?.[column] ?? column];
    cells[column] = cellOf(value);
  }
  return cells;
}

function csv(lines: string[][]): string {
  return `${lines.map((line) => line.map(csvCell).join(",")).join("\r\n")}\r\n`;
}

/** The download's name: the source's own name, marked as its refused rows. */
export function refusedRowsName(sourceName: string): string {
  const base = sourceName.replace(/\.(csv|json)$/i, "").trim() || "import-run";
  return `${base}-refused-rows.csv`;
}

export function refusedRowsCsv(batch: Pick<ImportBatch, "kind" | "source_name">, receipts: ImportReceipt[]): { name: string; text: string } {
  const refused = receipts.filter((r) => r.action === "rejected");
  const name = refusedRowsName(batch.source_name);
  if ((batch.kind ?? "historical") === "historical" && refused.length && refused.every(isTemplateReceipt)) {
    const byId = new Map(receipts.filter(isTemplateReceipt).map((r) => [composite(r.entity_kind, r.import_key), r] as const));
    const scope = withParents(refused.map((r) => composite(r.entity_kind, r.import_key)),
      (id) => { const r = byId.get(id)!; return parentsOf("historical", r.entity_kind, templateCells(r)); }, (id) => byId.has(id));
    const rows = scope.map((id, index) => [byId.get(id)!, index] as const)
      .sort(([a, i], [b, j]) => KIND_ORDER.indexOf(a.entity_kind) - KIND_ORDER.indexOf(b.entity_kind) || i - j).map(([r]) => r);
    return { name, text: csv([[...RECORD_COLUMNS, "reason"], ...rows.map((r) => {
      const cells = templateCells(r);
      return [...RECORD_COLUMNS.map((c) => cells[c] ?? ""), r.action === "rejected" ? r.reason ?? "" : ""];
    })]) };
  }
  const recorded = refused.map((r) => receiptRow(r)?.data ?? sourceEvidence(r.source_ref));
  const fields = [...new Set(recorded.flatMap((data) => Object.keys(data)))].filter((f) => !["entity_kind", "import_key", "reason"].includes(f));
  return { name, text: csv([["entity_kind", "import_key", "reason", ...fields],
    ...refused.map((r, i) => [r.entity_kind, r.import_key, r.reason ?? "", ...fields.map((f) => cellOf(recorded[i][f]))])]) };
}
